import { useEffect, useState, useMemo, useId } from 'react';
import ReactModal from 'react-modal';

import CustomSelect from 'components/custom-select';
import { ConfirmacaoDaAcao, ResultadoDaAcao } from 'components/task-item/modal-actions/moldura-da-acao';

import { toast } from 'react-toastify';
import { actionLog } from 'services/actionlog.service';
import { getLinkedIndicators } from 'services/profile.service';
import { LinkedIndicators_Indicadores } from 'types/user';

/** Placeholder que o aggregate `indicadores_vinculados` anexa à lista de todo cadastrante. */
const PLACEHOLDER_INDICATOR_COD = 1;

interface Props {
  callback: (action: boolean) => void;
  id: string;
  /**
   * Chave (F+matrícula) do DONO da realização. Os indicadores são os do acordo dele —
   * não os de quem está com o modal aberto. Sem isso o gestor imediato gravaria um
   * indicador da própria carteira na realização do funcionário.
   */
  owner?: string;
}

export default function ModalChangeIndicator({ callback, id, owner }: Props) {
  ReactModal.setAppElement('#modal');

  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState<any>();
  const [success, setSuccess] = useState<boolean | undefined>();
  const [indicator, setIndicator] = useState<{ label: string; value: number | string } | undefined>();

  const campoId = useId();

  const [indicatorsList, setIndicatorsList] = useState<LinkedIndicators_Indicadores[] | undefined>();

  // O placeholder sai da lista (reselecioná-lo seria um no-op) e os repetidos também:
  // `indicadores_vinculados__c` traz ~9% dos pares (chave, cod) duplicados, sempre com
  // o mesmo nome, então colapsar por `cod` nunca esconde um rótulo distinto.
  const options = useMemo(() => {
    const seen = new Set<string>();

    return (indicatorsList ?? [])
      .filter(item => Number(item.cod) !== PLACEHOLDER_INDICATOR_COD)
      .filter(item => {
        const key = String(item.cod);
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      })
      .map(item => ({ label: item.nome, value: item.cod }));
  }, [indicatorsList]);

  const formValid = useMemo(() => {
    return !!indicator && !!indicator?.value && indicator?.value;
  }, [indicator]);

  const closeModal = () => {
    callback(typeof success === 'boolean' && !!response ? true : false);
  };

  useEffect(() => {
    async function loadData() {
      const data = await getLinkedIndicators(owner);

      setIndicatorsList(data?.indicadores?.length ? data.indicadores : []);
    }

    loadData();
  }, [owner]);

  const handleSubmit = async () => {
    if (!formValid || sending) return;

    setSending(true);

    try {
      const { action } = await actionLog('me', 'atualizar_indicador', {
        cod: Number(indicator?.value),
        nome: indicator?.label,
        taskId: id,
      });

      const _success = action?.attributes?.status === 'OK';

      setSuccess(_success);
      setResponse(action);
      setSending(false);
    } catch (error) {
      toast.error(`Ocorreu um erro ao registrar sua ação. Por favor, tente novamente em alguns minutos.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      setSending(false);
    }
  };

  if (typeof id !== 'string' || !id.length) return <></>;

  if (typeof success === 'boolean' && !!response) {
    const atualizou = response.attributes.status === 'OK';

    return (
      <ResultadoDaAcao
        title={atualizou ? 'Mandou bem!' : 'Não foi possível atualizar a realização!'}
        semIlustracao={!atualizou}
        onClose={() => closeModal()}
      >
        {response.attributes.systemMessage}
      </ResultadoDaAcao>
    );
  }

  return (
    <ConfirmacaoDaAcao
      title="Atualize o Indicador desta Realização"
      subtitle="Atualize o indicador da realização para o mais adequado a sua realização."
      size="md"
      closeLabel="Fechar"
      onClose={() => {
        if (!sending) closeModal();
      }}
      backDisabled={sending}
      onConfirm={() => handleSubmit()}
      confirmLabel="Atualizar"
      confirmDisabled={!formValid || sending}
    >
      <div className="pb-1">
        <label htmlFor={campoId} className="mb-1.5 block text-jj-label font-semibold text-content-soft">
          Indicador
        </label>
        {!indicatorsList || options.length ? (
          <CustomSelect
            variant="light"
            inputId={campoId}
            ariaLabel="Indicador"
            value={indicator}
            placeholder={'Selecione o indicador'}
            options={indicatorsList ? options : [{ label: 'Carregando...', value: '' }]}
            onChange={(e: any) => [setIndicator(e)]}
          />
        ) : (
          <p className="m-0 text-content">
            Não há indicadores vinculados ao funcionário que cadastrou esta realização, portanto não é possível atualizar o
            indicador.
          </p>
        )}
      </div>
    </ConfirmacaoDaAcao>
  );
}
